import React, { useEffect, useState, useMemo } from 'react';
import { delayRender, continueRender, useCurrentFrame, useVideoConfig } from 'remotion';
import { SpeakerLabel, SpeakerLabelElementType } from '../../MainPlayer/types/Element/VideoElement';
import SpeakerLabelText from './SpeakerLabelText';
import { SelectionOutline } from '../Outlines/SelectionOutline';
import { SelectionElementType } from '../Outlines/types';
import { getTransitionSpeakerLabelInStyles } from '../../libs/transitions/transitionSpeakerLabelIn';
import { getTransitionSpeakerLabelOutStyles } from '../../libs/transitions/transitionSpeakerLabelOut';
import { SpeakerLabelTransitionEffect } from '../../libs/transitions/types';
import { useSelectionOutline } from '../../MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useSelectionOutline';
import { classnames } from '@/libs/utils';
import { loadRemotionFont } from '@/libs/fonts';

export default function SpeakerLabelContainer({
  speakerLabel,
  canEditSpeakerLabel,
  speakerId,
  layoutChangeFrame,
  isAudiogram,
  isSmallVideo = false
}: {
  speakerLabel?: SpeakerLabel;
  canEditSpeakerLabel: boolean;
  speakerId: string;
  layoutChangeFrame?: number;
  isAudiogram: boolean;
  isSmallVideo?: boolean;
}) {
  const [handle] = useState(() => delayRender('Loading Assets...', { retries: 3 }));
  const [isHovered, setIsHovered] = useState(false);
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const { selectedElement } = useSelectionOutline();

  // Animated speaker cards always have defined transitions object
  const useAnimatedSpeakerCards = !isAudiogram && !!speakerLabel?.transitions?.in;

  const isSelected = canEditSpeakerLabel && selectedElement === SelectionElementType.SPEAKER_LABEL;

  useEffect(() => {
    if (
      speakerLabel?.type === SpeakerLabelElementType.CONTAINER &&
      speakerLabel?.font?.location &&
      speakerLabel?.font?.name
    ) {
      const fontPromises = [loadRemotionFont(speakerLabel.font)];

      // Load title font if it's different from the name font
      if (
        speakerLabel.titleFont?.location &&
        speakerLabel.titleFont?.name &&
        speakerLabel.titleFont.location !== speakerLabel.font.location
      ) {
        fontPromises.push(loadRemotionFont(speakerLabel.titleFont));
      }

      Promise.all(fontPromises)
        .then(() => {
          // eslint-disable-next-line no-console
          console.log('Fonts loaded - ', speakerLabel.font?.name, speakerLabel.titleFont?.name);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('Error loading fonts - ', speakerLabel.font?.name, speakerLabel.titleFont?.name, error);
        })
        .finally(() => {
          continueRender(handle);
        });
    } else {
      continueRender(handle);
    }
  }, [
    handle,
    speakerLabel?.font?.location,
    speakerLabel?.font?.name,
    speakerLabel?.titleFont?.location,
    speakerLabel?.titleFont?.name,
    speakerLabel?.type
  ]);

  const selectionData = useMemo(() => ({ speakerId, type: SelectionElementType.SPEAKER_LABEL as const }), [speakerId]);

  const animationStyles = useMemo(() => {
    if (!useAnimatedSpeakerCards) {
      return {};
    }

    // In edit mode: override animations when hovered or selected to allow editing
    if (canEditSpeakerLabel && (isHovered || isSelected)) {
      return {
        opacity: 1,
        pointerEvents: 'auto' as const
      };
    }

    if (isSmallVideo) {
      return { opacity: 0, pointerEvents: 'none' as const };
    }

    if (!speakerLabel?.transitions) {
      return {};
    }

    const { transitions } = speakerLabel;

    // Calculate speaker-relative frame
    let speakerRelativeFrame = frame;

    if (transitions.showAgainOnNewSpeaker && layoutChangeFrame !== undefined) {
      speakerRelativeFrame = frame - layoutChangeFrame;
    }

    const inStyles = getTransitionSpeakerLabelInStyles({
      fps,
      frame: speakerRelativeFrame,
      animationType: transitions.in || SpeakerLabelTransitionEffect.None,
      showAfter: transitions.showAfter,
      hideAfter: transitions.hideAfter,
      duration: transitions.duration,
      showAgainOnNewSpeaker: transitions.showAgainOnNewSpeaker
    });

    const outStyles =
      transitions.hideAfter !== undefined
        ? getTransitionSpeakerLabelOutStyles({
            fps,
            frame: speakerRelativeFrame,
            animationType: transitions.out || SpeakerLabelTransitionEffect.None,
            hideAfter: transitions.hideAfter,
            showAfter: transitions.showAfter,
            duration: transitions.duration
          })
        : {};

    // Merge styles: exit animation takes precedence over entrance animation
    return { ...inStyles, ...outStyles };
  }, [
    useAnimatedSpeakerCards,
    isSmallVideo,
    canEditSpeakerLabel,
    isHovered,
    isSelected,
    speakerLabel,
    frame,
    fps,
    layoutChangeFrame
  ]);

  if (!speakerLabel) {
    return null;
  }

  if (useAnimatedSpeakerCards && canEditSpeakerLabel) {
    return (
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '20%', // Cover bottom 20% of video for hover detection
          pointerEvents: 'auto',
          zIndex: 9 // Just below the actual speaker label (zIndex: 10)
        }}
        data-testid="speaker-label-container-hover-detection"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          style={{
            ...speakerLabel?.style,
            ...animationStyles
          }}
          data-testid="speaker-label-container"
          className={classnames({ '!overflow-visible': canEditSpeakerLabel })}
        >
          {speakerLabel?.elements?.map((element, index) => {
            return <SpeakerLabelText element={element} key={index} />;
          })}
          <SelectionOutline
            positions={{ top: 0, left: 0, width: '100%', height: '100%' }}
            updatePositions={() => {}}
            onOutlineRelease={() => {}}
            allowResize={false}
            elementType={SelectionElementType.SPEAKER_LABEL}
            data={selectionData}
          />
        </div>
      </div>
    );
  }

  // Playback mode: no hover detection needed
  return (
    <div
      style={{ ...speakerLabel?.style, ...animationStyles }}
      data-testid="speaker-label-container"
      className={classnames({ '!overflow-visible': canEditSpeakerLabel })}
    >
      {speakerLabel?.elements?.map((element, index) => {
        return <SpeakerLabelText element={element} key={index} />;
      })}
      {!useAnimatedSpeakerCards && canEditSpeakerLabel && (
        <SelectionOutline
          positions={{ top: 0, left: 0, width: '100%', height: '100%' }}
          updatePositions={() => {}}
          onOutlineRelease={() => {}}
          allowResize={false}
          elementType={SelectionElementType.SPEAKER_LABEL}
          data={selectionData}
        />
      )}
    </div>
  );
}
